import React, { useId } from 'react';
import { STANDARD, OWN } from 'components/frank/utils/manufacturingLinesSource.js';

// The quote-wide "Standard Manufacturing Lines vs my own account" control, a radio group so BOTH choices are
// always visible and the selected one is unambiguous:
//
//   'standard' -- the shared example account (QUOTE_BUILDER_EXAMPLE_ACCOUNT), the default.
//   'own'      -- the logged-in owner's OWN configured machines and materials.
//
// One horizontal row, sitting beside the filter island. Standard is NOT the user's own data, so selecting it is
// flagged as a WARNING (brand-accent frame -- "this is not yours, use yours"); the owner's own account is the
// calm, expected state. The option labels already read "... Manufacturing Lines", so no separate visible label
// is needed -- the group is named for assistive tech through aria-label.
//
// Rendered on the /mcc header row and inside the guided-setup wizard; both drive the same quote-wide choice held
// on FrankQuoteBuilderPage, which persists it and remounts the sections so they re-solve against the new source.
const OPTIONS = [
  { value: STANDARD, labelKey: 'quote_builder.manufacturing_lines_source_standard' },
  { value: OWN, labelKey: 'quote_builder.manufacturing_lines_source_own' },
];

const StandardManufacturingLinesToggle = ({ value, onChange }) => {
  // A distinct radio-group name per instance (the header control and the wizard control can both be in the DOM),
  // so native mutual exclusion cannot pull the two apart -- each still reflects the one shared value.
  const groupName = useId();
  const warning = value === STANDARD;

  return (
    <div
      role="radiogroup"
      aria-label={t('quote_builder.manufacturing_lines_source_label')}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '16px',
        padding: '6px 12px',
        borderRadius: '8px',
        border: `2px solid ${warning ? 'var(--brand)' : 'var(--rule)'}`,
        background: warning ? 'rgba(235, 84, 36, 0.08)' : 'var(--surface-muted)',
        accentColor: 'var(--brand)',
        fontSize: '14px',
        color: 'var(--ink)',
      }}
      data-manufacturing-lines-source={value}
    >
      {OPTIONS.map((option) => {
        const active = value === option.value;
        return (
          <label
            key={option.value}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              // Bootstrap gives every label a 5px bottom margin; under flex align-items:center that margin
              // biases the whole row upward inside the framed group. Zero it so the row sits on centre.
              margin: 0,
              // Bootstrap zeroes every horizontal radio margin, so this gap is the only spacing between the
              // radio and its label -- 6px read as cramped, so give the control a little breathing room.
              gap: '9px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              fontWeight: active ? 700 : 500,
              color: active ? (warning ? 'var(--brand-dark)' : 'var(--ink)') : 'var(--ink-soft)',
            }}
          >
            <input
              type="radio"
              name={groupName}
              value={option.value}
              checked={active}
              onChange={() => onChange(option.value)}
              data-manufacturing-lines-source-option={option.value}
              // Bootstrap gives radios a 4px top margin (margin: 4px 0 0); under flex align-items:center that
              // pushes the control 2px below the text's vertical centre. Zero it so the radio sits on centre.
              style={{ margin: 0 }}
            />
            <span>{t(option.labelKey)}</span>
          </label>
        );
      })}
    </div>
  );
};

export default StandardManufacturingLinesToggle;
